import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth as useOIDCAuth } from 'react-oidc-context';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import axios from 'axios';

/**
 * Google認証（Cognito）コールバックページ
 * Google認証完了後にリダイレクトされるページ
 */
const CognitoCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const oidcAuth = useOIDCAuth();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const handleGoogleCallback = async () => {
      try {
        console.log('🔄 [COGNITO CALLBACK] Processing Google authentication callback...');

        // OIDCの認証状態をチェック
        if (oidcAuth.isLoading) {
          console.log('⏳ [COGNITO CALLBACK] OIDC is still loading...');
          return;
        }

        if (oidcAuth.error) {
          console.error('❌ [COGNITO CALLBACK] OIDC error:', oidcAuth.error);
          setError('認証中にエラーが発生しました。もう一度お試しください。');
          setIsProcessing(false);
          return;
        }

        if (!oidcAuth.isAuthenticated || !oidcAuth.user) {
          console.warn('⚠️ [COGNITO CALLBACK] Not authenticated or no user info');
          setError('認証情報が取得できませんでした。もう一度お試しください。');
          setIsProcessing(false);
          return;
        }

        // CognitoのID Tokenを取得
        const cognitoIdToken = oidcAuth.user.id_token;
        if (!cognitoIdToken) {
          console.error('❌ [COGNITO CALLBACK] No ID token');
          setError('IDトークンが取得できませんでした。');
          setIsProcessing(false);
          return;
        }

        console.log('✅ [COGNITO CALLBACK] Cognito ID token obtained');

        // バックエンドにID Tokenを送信してJWTトークンを取得
        const response = await axios.post('/api/auth/google/callback', {
          idToken: cognitoIdToken,
        });

        console.log('✅ [COGNITO CALLBACK] Backend authentication successful:', response.data);

        // JWTトークンがCookieに設定されているはず
        // ユーザーロールに応じてリダイレクト（window.location.hrefで強制リロード）
        const userRole = response.data.data.user.userRole;
        console.log('🔀 [COGNITO CALLBACK] Redirecting user with role:', userRole);

        // window.location.href を使用してページ全体をリロード
        // これにより useAuth の初期化処理が再実行され、認証状態が正しく反映される
        if (userRole === 'student') {
          window.location.href = '/chat';
        } else if (userRole === 'company_admin' || userRole === 'hr' || userRole === 'employee') {
          window.location.href = '/dashboard';
        } else if (userRole === 'admin') {
          window.location.href = '/admin/dashboard';
        } else {
          window.location.href = '/';
        }
      } catch (err: any) {
        console.error('❌ [COGNITO CALLBACK] Error:', err);
        const errorMessage =
          err.response?.data?.error?.message ||
          err.message ||
          'Google認証に失敗しました。';
        setError(errorMessage);
        setIsProcessing(false);
      }
    };

    // ルートパスが /auth/callback の場合のみ処理を実行
    if (window.location.pathname === '/auth/callback') {
      handleGoogleCallback();
    }
  }, [
    oidcAuth.isLoading,
    oidcAuth.error,
    oidcAuth.isAuthenticated,
    oidcAuth.user,
    navigate,
  ]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: 3,
      }}
    >
      {isProcessing ? (
        <>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 3 }}>
            認証処理中...
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            しばらくお待ちください
          </Typography>
        </>
      ) : error ? (
        <>
          <Alert severity="error" sx={{ maxWidth: 600, mb: 3 }}>
            {error}
          </Alert>
          <Typography
            variant="body2"
            color="primary"
            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => navigate('/auth/login')}
          >
            ログイン画面に戻る
          </Typography>
        </>
      ) : null}
    </Box>
  );
};

export default CognitoCallbackPage;
